const ALLOWED_PATTERN = /^[0-9.\s+\-*/()=]+$/

const PRECEDENCE = {
    u: 3,
    '*': 2,
    '/': 2,
    '+': 1,
    '-': 1,
}

function normalizeInput(str) {
    return String(str).trim().replace(/=+\s*$/, '').replace(/\s+/g, '')
}

/** Strip invalid characters while keeping math operators for in-progress expressions. */
export function sanitizePriceMathInput(val) {
    if (val === '' || val === null || val === undefined) {
        return ''
    }

    return String(val).replace(/[^0-9.\s+\-*/()=]/g, '')
}

export function isMathExpression(str) {
    if (str === '' || str === null || str === undefined) {
        return false
    }

    const s = normalizeInput(str)

    if (!s || !ALLOWED_PATTERN.test(s)) {
        return false
    }

    if (/[+*/()]/.test(s)) {
        return true
    }

    if (/\d-\d/.test(s)) {
        return true
    }

    const minusIdx = s.indexOf('-')
    if (minusIdx > 0) {
        return true
    }

    return false
}

export function formatPriceMathResult(value) {
    const rounded = Math.round(value * 1000) / 1000

    if (Number.isInteger(rounded)) {
        return String(rounded)
    }

    return parseFloat(rounded.toFixed(3)).toString()
}

function tokenize(expr) {
    const tokens = []
    let i = 0

    while (i < expr.length) {
        const c = expr[i]

        if ((c >= '0' && c <= '9') || c === '.') {
            let num = c
            i++

            while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) {
                num += expr[i++]
            }

            const parts = num.split('.')
            if (num === '.' || parts.length > 2) {
                return { error: 'Invalid number in expression.' }
            }

            const value = parseFloat(num)
            if (Number.isNaN(value)) {
                return { error: 'Invalid number in expression.' }
            }

            tokens.push({ type: 'number', value })
            continue
        }

        if (c === '(') {
            tokens.push({ type: 'lparen' })
            i++
            continue
        }

        if (c === ')') {
            tokens.push({ type: 'rparen' })
            i++
            continue
        }

        if ('+-*/'.includes(c)) {
            const prev = tokens[tokens.length - 1]
            const unary = c === '-' && (!prev || prev.type === 'op' || prev.type === 'lparen')

            tokens.push({ type: 'op', value: c, unary })
            i++
            continue
        }

        return { error: 'Expression contains invalid characters.' }
    }

    if (tokens.length === 0) {
        return { error: 'Expression is empty.' }
    }

    return { tokens }
}

function toRpn(tokens) {
    const output = []
    const operators = []

    for (let i = 0; i < tokens.length; i++) {
        const token = tokens[i]

        if (token.type === 'number') {
            output.push(token)
            continue
        }

        if (token.type === 'op' && token.unary) {
            operators.push({ type: 'op', value: 'u' })
            continue
        }

        if (token.type === 'op') {
            while (
                operators.length > 0 &&
                operators[operators.length - 1].type === 'op' &&
                PRECEDENCE[operators[operators.length - 1].value] >= PRECEDENCE[token.value]
            ) {
                output.push(operators.pop())
            }

            operators.push({ type: 'op', value: token.value })
            continue
        }

        if (token.type === 'lparen') {
            operators.push(token)
            continue
        }

        if (token.type === 'rparen') {
            while (operators.length > 0 && operators[operators.length - 1].type !== 'lparen') {
                output.push(operators.pop())
            }

            if (operators.length === 0) {
                return { error: 'Mismatched parentheses.' }
            }

            operators.pop()
        }
    }

    while (operators.length > 0) {
        const op = operators.pop()
        if (op.type === 'lparen' || op.type === 'rparen') {
            return { error: 'Mismatched parentheses.' }
        }
        output.push(op)
    }

    return { output }
}

function evaluateRpn(output) {
    const stack = []

    for (let i = 0; i < output.length; i++) {
        const token = output[i]

        if (token.type === 'number') {
            stack.push(token.value)
            continue
        }

        if (token.value === 'u') {
            if (stack.length < 1) {
                return { error: 'Invalid expression.' }
            }
            stack.push(-stack.pop())
            continue
        }

        if (stack.length < 2) {
            return { error: 'Invalid expression.' }
        }

        const b = stack.pop()
        const a = stack.pop()
        let result

        switch (token.value) {
            case '+':
                result = a + b
                break
            case '-':
                result = a - b
                break
            case '*':
                result = a * b
                break
            case '/':
                if (b === 0) {
                    return { error: 'Cannot divide by zero.' }
                }
                result = a / b
                break
            default:
                return { error: 'Invalid operator.' }
        }

        if (!Number.isFinite(result)) {
            return { error: 'Expression did not produce a valid number.' }
        }

        stack.push(result)
    }

    if (stack.length !== 1) {
        return { error: 'Invalid expression.' }
    }

    return { value: stack[0] }
}

export function evaluateMathExpression(str) {
    if (str === '' || str === null || str === undefined) {
        return { ok: false, error: 'Expression is empty.' }
    }

    const normalized = normalizeInput(str)

    if (!normalized) {
        return { ok: false, error: 'Expression is empty.' }
    }

    if (!ALLOWED_PATTERN.test(normalized)) {
        return { ok: false, error: 'Expression contains invalid characters. Only numbers and + - * / ( ) are allowed.' }
    }

    const tokenResult = tokenize(normalized)
    if (tokenResult.error) {
        return { ok: false, error: tokenResult.error }
    }

    const rpnResult = toRpn(tokenResult.tokens)
    if (rpnResult.error) {
        return { ok: false, error: rpnResult.error }
    }

    const evalResult = evaluateRpn(rpnResult.output)
    if (evalResult.error) {
        return { ok: false, error: evalResult.error }
    }

    if (!Number.isFinite(evalResult.value) || Number.isNaN(evalResult.value)) {
        return { ok: false, error: 'Expression did not produce a valid number.' }
    }

    return { ok: true, value: evalResult.value }
}
